import { css } from '@emotion/react'
import { focusBoxShadow, Heading, Icon, IconImage, Theme, useTheme } from 'bold-ui'

export enum MapaSidebarNavItemEnum {
  BUSCA = 'BUSCA',
  AREAS = 'AREAS',
  AJUDA = 'AJUDA',
}

interface MapaSidebarNavItemProps {
  navItem: MapaSidebarNavItemEnum
  selectedNavItem: MapaSidebarNavItemEnum
  onSelect(navItem: MapaSidebarNavItemEnum): void
}

export function MapaSidebarNavItem(props: Readonly<MapaSidebarNavItemProps>) {
  const { navItem, selectedNavItem, onSelect } = props

  const isSelected = navItem === selectedNavItem

  const theme = useTheme()
  const styles = createStyles(theme, isSelected)

  const { icon, label } = MAPA_SIDEBAR_NAV_ITEMS[navItem]

  return (
    <button css={styles.button} onClick={() => onSelect(navItem)}>
      <div css={styles.iconWrapper}>
        <Icon icon={icon} style={styles.icon} />
      </div>
      <Heading level={5} style={styles.label}>
        {label}
      </Heading>
    </button>
  )
}

export const MAPA_SIDEBAR_NAV_ITEMS: Record<MapaSidebarNavItemEnum, { label: string; icon: IconImage }> = {
  [MapaSidebarNavItemEnum.BUSCA]: {
    label: 'Busca',
    icon: 'zoomOutline',
  },
  [MapaSidebarNavItemEnum.AREAS]: {
    label: 'Áreas',
    icon: 'mapOutline',
  },
  [MapaSidebarNavItemEnum.AJUDA]: {
    label: 'Ajuda',
    icon: 'questionMarkOutline',
  },
}

export const MAPA_SIDEBAR_NAV_ITEM_ROUTE: Record<MapaSidebarNavItemEnum, string> = {
  [MapaSidebarNavItemEnum.BUSCA]: 'busca',
  [MapaSidebarNavItemEnum.AREAS]: 'areas',
  [MapaSidebarNavItemEnum.AJUDA]: 'ajuda',
}

export function parseMapaSidebarNavItem(routeParam: string | undefined): MapaSidebarNavItemEnum {
  return (
    Object.values(MapaSidebarNavItemEnum).find((navItem) => MAPA_SIDEBAR_NAV_ITEM_ROUTE[navItem] === routeParam) ??
    MapaSidebarNavItemEnum.BUSCA
  )
}

const createStyles = (theme: Theme, isSelected: boolean) => ({
  button: css`
    position: relative;
    width: 100%;
    padding: 0.5rem 0;
    border: 0;
    background: ${theme.pallete.surface.main};
    cursor: pointer;
    display: grid;
    justify-items: center;
    justify-content: center;
    gap: 0.25rem;
    outline: 0;
    border-radius: 2px;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 4px;
      background: ${isSelected ? theme.pallete.primary.main : 'transparent'};
    }

    &:active::after {
      background: ${theme.pallete.primary.main};
    }

    &:hover > div {
      background: ${isSelected ? theme.pallete.primary.c90 : theme.pallete.gray.c90};
    }

    &:active > div {
      background: ${theme.pallete.primary.c90};
    }

    &:active > div > svg {
      fill: ${theme.pallete.primary.main};
    }

    &:active > h5 {
      color: ${theme.pallete.primary.main};
    }

    &:focus {
      box-shadow: ${focusBoxShadow(theme, 'primary', 'inset')};
    }

    &:active {
      box-shadow: none;
    }
  `,
  iconWrapper: css`
    width: 2rem;
    height: 2rem;
    border-radius: ${theme.radius.button}px;
    background: ${isSelected ? theme.pallete.primary.c90 : theme.pallete.surface.main};
    display: grid;
    align-items: center;
    justify-content: center;
  `,
  icon: css`
    fill: ${isSelected ? theme.pallete.primary.main : theme.pallete.text.main};
  `,
  label: css`
    color: ${isSelected ? theme.pallete.primary.main : theme.pallete.text.main};
  `,
})
